import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, Users, UserPlus, Wrench, Settings, Layers, LogOut, Menu, X, Bell, Wallet, ShieldCheck,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import Logo from "../components/Logo";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, testid: "nav-dashboard" },
  { to: "/admin/payouts", label: "Payouts", icon: Wallet, testid: "nav-payouts" },
  { to: "/admin/amc", label: "AMC", icon: ShieldCheck, testid: "nav-amc" },
  { to: "/admin/bookings", label: "Bookings", icon: Calendar, testid: "nav-bookings" },
  { to: "/admin/applications", label: "Applications", icon: UserPlus, testid: "nav-applications" },
  { to: "/admin/catalog", label: "Catalog", icon: Layers, testid: "nav-catalog" },
  { to: "/admin/technicians", label: "Technicians", icon: Wrench, testid: "nav-technicians" },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB] flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#0F1C35] text-slate-200 flex flex-col transform transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <Logo to="/admin" size="sm" testId="admin-logo" />
            <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Admin</div>
          </div>
          <button className="lg:hidden text-slate-300" onClick={() => setMobileOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              data-testid={n.testid}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-white/10 text-white font-semibold"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 px-4 py-4">
          <div className="text-xs text-slate-400">Signed in as</div>
          <div className="text-sm font-semibold text-white truncate">{user?.email}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">{user?.role?.replace(/_/g, " ")}</div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-white/5 hover:bg-white/10 text-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button className="lg:hidden text-slate-700" onClick={() => setMobileOpen(true)} data-testid="admin-mobile-menu">
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="font-display font-bold text-lg text-slate-900" data-testid="admin-page-title">{title}</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/"
                className="text-xs font-semibold text-slate-500 hover:text-[#1A3C6E] px-3 py-2 rounded-lg"
                data-testid="admin-back-to-site"
              >
                ← Back to site
              </Link>
              <span className="relative">
                <Bell className="w-5 h-5 text-slate-500" />
              </span>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
